import { Link } from '@cluey/cluey-components';
import { useQuery } from '@tanstack/react-query';
import { isNull } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';

import useNextPaymentQuery from '../../../hooks/queries/useNextPaymentQuery';
import { useGetSessions } from '../../../hooks/queries/useSessionQuery';
import { useGetVouchers } from '../../../hooks/queries/useVoucherQuery';
import { CreditCardIcon, ExclamationMarkIcon, TicketIcon } from '../../../icons';
import { DollarIcon } from '../../../icons/DollarIcon';
import { InvoiceService } from '../../../services/InvoicesService';
import { STUDENT_SESSION_TYPE_KEYS } from '../../../types/hubEnums';
import { Session } from '../../../types/hubInterfaces';
import { PROMO_DISCOUNT_MAPPING } from '../../../util/discountsMapping';
import { isPaymentFailed, isPaymentPending, isPaymentSucceed } from '../../../util/helpers';
import { PATH_HUB_INVOICE_HISTORY } from '../../../util/pagePath';
import { getDaysAway } from '../../../views/account/PricingPlanDetailsPage/components/NextPaymentSection/helper';
import { InlineLoader } from '../../../views/hub/auth2/common';

interface Props {
  session: Session;
}

// Will asynchronously fetch the invoice link from Zuora
// and render if found
const InvoiceLink = ({ session }: { session: Session }) => {
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [viewClicked, setViewClicked] = useState(false);

  const { data: usedVouchers, isLoading: isUsedVoucherLoading } = useGetVouchers('Used');
  const { data: cancelledSessions, isLoading: isCancelledSessionsLoading } = useGetSessions({
    filter: 'cancelled',
    enabled: !isUsedVoucherLoading,
  });

  const {
    data: version,
    isLoading,
    fetchStatus,
  } = useQuery({
    queryKey: ['ZUORA_LINK', invoiceId],
    queryFn: () => InvoiceService.getInvoiceFileInfo(invoiceId!),
    enabled: !!invoiceId && viewClicked,
    retry: false,
  });

  useEffect(() => {
    if (!isUsedVoucherLoading && !isCancelledSessionsLoading) {
      let targetInvoiceId: string | null = null;
      if (!isNull(session.studentSessionType) && session.studentSessionType === STUDENT_SESSION_TYPE_KEYS.MAKE_UP) {
        const usedVoucher = usedVouchers?.find((voucher) => voucher.usedStudentSession === session.studentSessionId);
        if (usedVoucher) {
          const cancelledSession = cancelledSessions?.find(
            (cancelledSession) => cancelledSession.studentSessionId === usedVoucher.originalStudentSession
          );
          if (cancelledSession?.invoiceId) {
            targetInvoiceId = cancelledSession.invoiceId;
          }
        }
      } else if (session.invoiceId) {
        targetInvoiceId = session.invoiceId;
      }

      if (targetInvoiceId) {
        setInvoiceId(targetInvoiceId);
      }
    }
  }, [session, isUsedVoucherLoading, isCancelledSessionsLoading, usedVouchers, cancelledSessions]);

  useEffect(() => {
    if (version?.[0]?.url && viewClicked) {
      window.open(version[0].url, '_blank');
    }
  }, [version, viewClicked]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!viewClicked) {
      setViewClicked(true);
    } else if (version?.[0]?.url) {
      window.open(version[0].url, '_blank');
    }
  };

  return (
    <>
      {(isUsedVoucherLoading || isCancelledSessionsLoading) && (
        <>
          <span className="ml-1 mr-2">|</span>
          <InlineLoader className="inline-block !h-3 !w-3" />
        </>
      )}
      {invoiceId && (
        <button onClick={handleClick} className="ml-1 text-blue-5">
          {isLoading && fetchStatus !== 'idle' ? (
            '| Preparing...'
          ) : (
            <>
              | <span className="underline underline-offset-4">View invoice</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

const createPaymentMessage = (session: Session, nextPayment: any) => {
  const nextPaymentSessions = nextPayment?.sessionInfo || [];
  const includedInNextPayment =
    isPaymentPending(session.paymentStatus) &&
    nextPaymentSessions.map((ses) => ses.studentSessionId).find((id) => id === session.studentSessionId);
  let paymentMessage = null;
  if (isPaymentSucceed(session.paymentStatus)) {
    const promo = PROMO_DISCOUNT_MAPPING[(session.discountCode || '').toUpperCase()];
    if (session.studentSessionType === STUDENT_SESSION_TYPE_KEYS.MAKE_UP) {
      paymentMessage = (
        <div>
          Booked with Cluey voucher
          <InvoiceLink session={session} />
        </div>
      );
    } else {
      paymentMessage = (
        <div>
          Paid{!!promo && ` ${promo}`}
          <InvoiceLink session={session} />
        </div>
      );
    }
  } else if (includedInNextPayment) {
    paymentMessage = (
      <div className="text-grey-6">
        <span className="font-semibold">${nextPayment.total}</span> Payment will be charged on{' '}
        <span className="font-semibold">
          {moment(nextPayment.paymentDate).format('MMM D')}{' '}
          {getDaysAway(nextPayment?.paymentDate, {
            wrapperFunction: (content, daysAway) => `(${daysAway > 1 ? `in ${daysAway} days` : content})`,
          })}
        </span>
      </div>
    );
  } else if (isPaymentFailed(session.paymentStatus)) {
    paymentMessage = (
      <div>
        <strong>Payment not received</strong>{' '}
        <Link className="!text-base underline underline-offset-4" to={PATH_HUB_INVOICE_HISTORY}>
          Update payment details
        </Link>
      </div>
    );
  } else {
    paymentMessage = 'Yet to be paid';
  }
  return { paymentMessage, includedInNextPayment };
};

const SessionPaymentInfo = ({ session }: Props) => {
  const { data } = useNextPaymentQuery();
  const content = createPaymentMessage(session, data);
  const getPaymentStatusIcon = ({ session }: { session: Session }) => {
    if (session.paymentStatus === 'Paid') {
      return session.studentSessionType === STUDENT_SESSION_TYPE_KEYS.MAKE_UP ? (
        <TicketIcon className="mt-0.5 fill-blood-orange-4 md:mt-0" width={20} height={20} />
      ) : (
        <DollarIcon className="mt-0.5 md:mt-0" width={16} height={16} />
      );
    } else if (session.paymentStatus === 'Failed') {
      return <ExclamationMarkIcon className="text-red-5" />;
    } else {
      return <CreditCardIcon height={16} width={16} className="align-bottom text-green-5" />;
    }
  };

  return (
    <div className="payment-indicator">
      <div className="payment-indicator--paid mb-0 flex items-center gap-2 text-base">
        {getPaymentStatusIcon({ session })}
        {content.paymentMessage}
      </div>
    </div>
  );
};
export default SessionPaymentInfo;
